import type { LucideIcon } from 'lucide-react'
import {
  LayoutDashboard,
  Baby,
  ClipboardList,
  History,
  Users,
  FileText,
  ScrollText,
  AudioWaveform,
  Ear,
  MessageCircle,
  UsersRound,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
} from 'lucide-react'
import { RiskLevel, QuestionnaireCategory } from '@/types/dashboard.types'

// ============================================
// RISK LEVEL CONFIGURATIONS
// ============================================

export const RISK_LEVEL_CONFIG = {
  [RiskLevel.LOW]: {
    label: 'Low Risk',
    color: '#22c55e', // green-500
    bgColor: '#dcfce7', // green-100
    borderColor: '#86efac', // green-300
    Icon: CheckCircle2,
    description: 'Development appears to be on track',
    /** Full badge: light tints + readable dark mode (no flat grey wash) */
    badgeClassName:
      'bg-emerald-500/15 text-emerald-900 border border-emerald-500/45 dark:bg-emerald-950/50 dark:text-emerald-200 dark:border-emerald-500/55',
    reportCardClassName: 'border-2 border-emerald-500 dark:border-emerald-400',
    headlineClassName: 'text-emerald-700 dark:text-emerald-400',
  },
  [RiskLevel.MID]: {
    label: 'Mid Risk',
    color: '#eab308', // yellow-500
    bgColor: '#fef9c3', // yellow-100
    borderColor: '#fde047', // yellow-300
    Icon: AlertTriangle,
    description: 'Some areas may need attention',
    badgeClassName:
      'bg-yellow-500/15 text-yellow-950 border border-yellow-500/45 dark:bg-yellow-950/45 dark:text-yellow-200 dark:border-yellow-500/55',
    reportCardClassName: 'border-2 border-yellow-500 dark:border-yellow-400',
    headlineClassName: 'text-yellow-700 dark:text-yellow-400',
  },
  [RiskLevel.HIGH]: {
    label: 'High Risk',
    color: '#ef4444', // red-500
    bgColor: '#fee2e2', // red-100
    borderColor: '#fca5a5', // red-300
    Icon: AlertCircle,
    description: 'Professional evaluation recommended',
    badgeClassName:
      'bg-red-500/15 text-red-900 border border-red-500/45 dark:bg-red-950/50 dark:text-red-200 dark:border-red-500/55',
    reportCardClassName: 'border-2 border-red-500 dark:border-red-400',
    headlineClassName: 'text-red-700 dark:text-red-400',
  },
}

// ============================================
// CATEGORY CONFIGURATIONS
// ============================================

export const CATEGORY_CONFIG = {
  [QuestionnaireCategory.SPEECH_SOUND]: {
    label: 'Speech Sound',
    shortLabel: 'Speech',
    Icon: AudioWaveform,
    color: '#3b82f6', // blue-500
    description: 'How clearly your child speaks and how sounds and words come across in everyday situations.',
  },
  [QuestionnaireCategory.LANGUAGE_COMPREHENSION]: {
    label: 'Language Comprehension',
    shortLabel: 'Understanding',
    Icon: Ear,
    color: '#8b5cf6', // violet-500
    description: 'Following directions, understanding words and questions, and listening in daily routines.',
  },
  [QuestionnaireCategory.EXPRESSIVE_LANGUAGE]: {
    label: 'Expressive Language',
    shortLabel: 'Expression',
    Icon: MessageCircle,
    color: '#ec4899', // pink-500
    description: 'Vocabulary, combining words, sentence length, and how your child expresses ideas.',
  },
  [QuestionnaireCategory.BEHAVIORAL]: {
    label: 'Behavioral Social',
    shortLabel: 'Flow',
    Icon: UsersRound,
    color: '#14b8a6', // teal-500
    description: 'Gestures, turn-taking, play, and how your child copes when others do not understand.',
  },
}

/** Filipino labels for assessment UI when language mode is Filipino */
export const CATEGORY_LABEL_FILIPINO: Record<QuestionnaireCategory, string> = {
  [QuestionnaireCategory.SPEECH_SOUND]: 'Linaw ng pagsasalita at tunog',
  [QuestionnaireCategory.LANGUAGE_COMPREHENSION]: 'Pag-unawa sa wika',
  [QuestionnaireCategory.EXPRESSIVE_LANGUAGE]: 'Paggamit ng salita at pangungusap',
  [QuestionnaireCategory.BEHAVIORAL]: 'Daloy ng komunikasyon at suporta',
}

// ============================================
// NAVIGATION ITEMS
// ============================================

export type DashboardNavItem = {
  label: string
  path: string
  icon: LucideIcon
  description: string
}

export const PARENT_NAV_ITEMS: DashboardNavItem[] = [
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
    description: 'Home and overview',
  },
  {
    label: 'My Children',
    path: '/dashboard/children',
    icon: Baby,
    description: 'Manage child profiles',
  },
  {
    label: 'Assessments',
    path: '/dashboard/assessment',
    icon: ClipboardList,
    description: 'Take and view assessments',
  },
  {
    label: 'History',
    path: '/dashboard/history',
    icon: History,
    description: 'Assessment history and results',
  },
]

export const ADMIN_NAV_ITEMS: DashboardNavItem[] = [
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: LayoutDashboard,
    description: 'System overview',
  },
  {
    label: 'Users',
    path: '/dashboard/admin/users',
    icon: Users,
    description: 'View all users',
  },
  {
    label: 'Questionnaires',
    path: '/dashboard/admin/questionnaires',
    icon: FileText,
    description: 'Manage questions',
  },
  {
    label: 'Audit Logs',
    path: '/dashboard/admin/audit-logs',
    icon: ScrollText,
    description: 'System activity logs',
  },
]

// ============================================
// AGE RANGES
// ============================================

export const AGE_RANGES = {
  MIN_MONTHS: 24,
  MAX_MONTHS: 60,
  MIN_YEARS: 2,
  MAX_YEARS: 5,
}

// ============================================
// ASSESSMENT CONFIGURATION
// ============================================

export const ASSESSMENT_CONFIG = {
  TOTAL_QUESTIONS: 40,
  QUESTIONS_PER_CATEGORY: 10,
  MULTIPLE_CHOICE_COUNT: 28,
  FREE_TEXT_COUNT: 12,
  ESTIMATED_TIME_MINUTES: 20,
}

// ============================================
// SCORING CONFIGURATION
// ============================================

export const SCORING_CONFIG = {
  MIN_SCORE: 0,
  MAX_SCORE: 4,
  SCORE_LABELS: {
    0: 'Never / Not Yet',
    1: 'Rarely',
    2: 'Sometimes',
    3: 'Often',
    4: 'Always / Very Well',
  },
}
